import React, { useEffect, useRef } from 'react';

/**
 * Proof-of-results carousel.
 *
 * It never stops turning: the track advances continuously and the visitor can
 * grab it (finger or mouse) to look at a specific photo, with the rotation
 * resuming the instant they let go. Vertical page scrolling is untouched —
 * `touch-action: pan-y` leaves it to the browser, and only horizontal drags
 * are captured here.
 */
const RESULT_IMAGES = ['https://cdn.magicpatterns.com/uploads/eE7XN35KbNJi2xcUddpW87/carrusel_1.jpg', 'https://cdn.magicpatterns.com/uploads/kFLapm9LSJ5uBKf5HgruGz/carrusel_2.jpg', 'https://cdn.magicpatterns.com/uploads/uwFH2zbJvMq9fA1HtGokeR/carrusel_3.jpg', 'https://cdn.magicpatterns.com/uploads/9WzncVCYsWXAkXMfJHdZxW/carrusel_4.jpg', 'https://cdn.magicpatterns.com/uploads/kKZkibqUXxUCCRyQEU7GUj/carrusel_5.jpg', 'https://cdn.magicpatterns.com/uploads/a8SfGdgoxrf551B1b3EYNV/carrusel_6.jpg', 'https://cdn.magicpatterns.com/uploads/p51GmhwPfCiK1g1ytf3wFx/carrusel_7.jpg', 'https://cdn.magicpatterns.com/uploads/n5mSUQkbwSuneKu4f9PdRU/carrusel_8.jpg', 'https://cdn.magicpatterns.com/uploads/2g4pAZzYujyFZmzSLD3Uou/carrusel_9.jpg'];

/** Pixels per second the carousel advances on its own. */
const AUTO_SPEED = 30;
/** Horizontal travel needed before a touch counts as a carousel drag. */
const DRAG_THRESHOLD = 6;
export const ResultsCarousel = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const isDraggingRef = useRef(false);
  const pointerDownRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startOffsetRef = useRef(0);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let last = performance.now();
    const render = () => {
      const half = track.scrollWidth / 2;
      if (half > 0) {
        // Keep the offset inside the first copy so the loop is seamless.
        offsetRef.current = (offsetRef.current % half + half) % half;
      }
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
    };
    const step = (now: number) => {
      const delta = Math.min(now - last, 100);
      last = now;
      if (!isDraggingRef.current && !prefersReducedMotion) {
        offsetRef.current += AUTO_SPEED * delta / 1000;
      }
      render();
      frame = requestAnimationFrame(step);
    };
    render();
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, []);
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerDownRef.current = true;
    startXRef.current = event.clientX;
    startYRef.current = event.clientY;
    startOffsetRef.current = offsetRef.current;
    if (event.pointerType === 'mouse') {
      isDraggingRef.current = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerDownRef.current) return;
    const deltaX = event.clientX - startXRef.current;

    // A touch only becomes a carousel drag once it is clearly horizontal;
    // otherwise the browser keeps scrolling the page vertically.
    if (!isDraggingRef.current) {
      const deltaY = event.clientY - startYRef.current;
      if (Math.abs(deltaX) < DRAG_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      isDraggingRef.current = true;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Capture is a nicety; dragging still works without it.
      }
    }
    offsetRef.current = startOffsetRef.current - deltaX;
  };
  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerDownRef.current = false;
    // Rotation picks up again immediately on release.
    isDraggingRef.current = false;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };
  return <div className="relative" data-id="element-385" data-mp="2lx1j4nlgn831">
      <div onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endDrag} onPointerCancel={endDrag} onPointerLeave={endDrag} className="-mx-4 touch-pan-y overflow-hidden md:-mx-6" role="region" aria-label="Resultados reales logrados con el Megapack" data-id="element-386" data-mp="2lvq4fsqh8fx5">
        <div ref={trackRef} className="flex w-max cursor-grab select-none will-change-transform active:cursor-grabbing" data-id="element-387" data-mp="2830exebqosva">
          {[0, 1].map(copy => <React.Fragment key={copy} data-id="element-388" data-mp="2gzsy8f8xgpy3">
              {RESULT_IMAGES.map((src, i) => <figure key={`${copy}-${src}`} className="mr-3 w-[132px] shrink-0 overflow-hidden rounded-[12px] border border-line bg-bg-card md:mr-4 md:w-[176px]" data-id="element-389" data-mp="2c5opprk0k9uw" data-mpi={i}>
                  <img src={src} alt={copy === 0 ? `Resultado real de colorimetría masculina ${i + 1}` : ''} aria-hidden={copy === 1} loading="lazy" decoding="async" draggable={false} width={176} height={220} className="h-[165px] w-full select-none object-cover md:h-[220px]" data-id="element-390" data-mp="1kdzdwc58kk01" />
                </figure>)}
            </React.Fragment>)}
        </div>
      </div>
      {/* Edge fades so the loop reads as continuous */}
      <div className="pointer-events-none absolute inset-y-0 -left-4 w-8 bg-gradient-to-r from-bg-base to-transparent md:-left-6 md:w-16" data-id="element-391" data-mp="1o2qr7evhk6g1" />
      <div className="pointer-events-none absolute inset-y-0 -right-4 w-8 bg-gradient-to-l from-bg-base to-transparent md:-right-6 md:w-16" data-id="element-392" data-mp="7ngvjld9fkpb" />
    </div>;
};